import { ReactNode, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import { unstable_useId as useId } from "@mui/material/utils";
import styled, { css } from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";
import { Button } from "@pages/AdminDashboard/ui/Button";

const { color, radius, shadow, statusTone, typeScale } = adminTokens;
const [, spaceSm, spaceMd, spaceLg] = adminTokens.spacing;

// The design's dialogs: 560px wide, 28px of padding, 24px clear at each side of
// a narrow content area, and 90px below the top of the content area.
const dialogWidth = 560;
const dialogPadding = 28;
const sideClearance = 24;
const topOffset = 90;
const stepBadgeSize = 22;
const stepGap = 10;
const footerTopPadding = 4;
// The design sets a dialog title in Light 24 (DIALOG-R3), the same open
// question as a card title (CARD-R7).
const titleSize = "24px";
const titleLineHeight = 1.25;
// The design's full-screen step on a phone: a 52px bar at the top with a 44px close
// control, the content padded 16px, and the actions in a bar at the bottom
// (PHONE-R8…R15).
const stepBarHeight = 52;
const stepBarPadding = 6;
const closeControlSize = 44;
const closeIconSize = 20;
const stepBarTitleSize = "16px";

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Eyebrow = styled.span<{ $isDestructive: boolean }>`
  color: ${({ $isDestructive }) => ($isDestructive ? statusTone.error.text : color.inkSoft)};
  font-family: ${typeScale.eyebrow.fontFamily};
  font-size: ${typeScale.eyebrow.fontSize};
  letter-spacing: ${typeScale.eyebrow.letterSpacing};
  text-transform: ${typeScale.eyebrow.textTransform};
`;

const Title = styled.h2`
  margin: 0;
  color: ${color.navy};
  font-family: ${adminFontFamily.light};
  font-size: ${titleSize};
  font-weight: normal;
  line-height: ${titleLineHeight};
  overflow-wrap: anywhere;
`;

// On a phone the steps sit on a white card, as the design's full-screen step
// sets them (PHONE-R38).
const phoneStepsRadius = 16;

const Steps = styled.ol.attrs({ role: "list" })<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spaceSm}px;
  margin: 0;
  padding: 0;
  list-style: none;

  ${({ $isPhone }) =>
    $isPhone &&
    css`
      padding: ${spaceLg}px;
      border: 1px solid ${color.border};
      border-radius: ${phoneStepsRadius}px;
      background-color: ${color.surface};
    `}
`;

const Step = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${stepGap}px;
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  line-height: ${typeScale.body.lineHeight};
`;

const StepBadge = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${stepBadgeSize}px;
  height: ${stepBadgeSize}px;
  border-radius: 50%;
  background-color: ${color.canvas};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: 12px;
`;

const Note = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
  line-height: ${typeScale.meta.lineHeight};
`;

const TopBar = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: 2px;
  height: ${stepBarHeight}px;
  padding: 0 ${stepBarPadding}px;
  border-bottom: 1px solid ${color.border};
  background-color: ${color.surface};
`;

const CloseControl = styled.button`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${closeControlSize}px;
  height: ${closeControlSize}px;
  padding: 0;
  border: 0;
  border-radius: ${radius.pill}px;
  background: transparent;
  color: ${color.navy};
  cursor: pointer;

  &:focus-visible {
    ${focusOutline}
  }

  &:disabled {
    color: ${color.disabledText};
    cursor: not-allowed;
  }
`;

const TopBarTitle = styled.span<{ $isDestructive: boolean }>`
  flex: 1;
  min-width: 0;
  color: ${({ $isDestructive }) => ($isDestructive ? statusTone.error.text : color.navy)};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${stepBarTitleSize};
`;

const StepContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spaceLg}px;
  min-height: 0;
  overflow-y: auto;
  padding: ${spaceLg}px;
`;

const BottomBar = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
  gap: ${spaceSm}px;
  padding: ${spaceMd}px ${spaceLg}px ${spaceLg}px;
  border-top: 1px solid ${color.border};
  background-color: ${color.surface};

  & > button {
    width: 100%;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${stepGap}px;
  padding-top: ${footerTopPadding}px;
`;

export interface ConfirmDialogProps {
  isOpen: boolean;
  eyebrow: string;
  title: string;
  dismissLabel: string;
  confirmLabel: string;
  pendingLabel: string;
  onConfirm: () => void;
  onDismiss: () => void;
  consequences?: string[];
  note?: string;
  isDestructive?: boolean;
  isPending?: boolean;
  isConfirmDisabled?: boolean;
  // The content area the scrim covers, which must be positioned (for example
  // `position: relative`); the whole page when not given.
  container?: HTMLElement | null;
  children?: ReactNode;
}

// Asks the admin to confirm one action after saying what it will do
// (DIALOG-R1…R15). While the confirmation is pending nothing can dismiss the
// dialog, so the request cannot be abandoned half way (XCUT-R1). A click on the
// scrim does not dismiss it (DIALOG-R16).
export const ConfirmDialog = ({
  isOpen,
  eyebrow,
  title,
  dismissLabel,
  confirmLabel,
  pendingLabel,
  onConfirm,
  onDismiss,
  consequences = [],
  note,
  isDestructive = false,
  isPending = false,
  isConfirmDisabled = false,
  container,
  children,
}: ConfirmDialogProps) => {
  const isPhone = useIsPhoneLayout();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const isScopedToContainer = Boolean(container);

  const handleClose = (_event: unknown, reason: string) => {
    if (reason !== "escapeKeyDown" || isPending) return;

    onDismiss();
  };

  // MUI focuses the wrapper around the panel; focus belongs on the panel itself
  // so the dialog and its name are announced (DIALOG-R13, DIALOG-R14).
  const focusPanel = () => panelRef.current?.focus();

  const body = (
    <>
      {children}
      {consequences.length > 0 && (
        <Steps $isPhone={isPhone}>
          {consequences.map((consequence, index) => (
            <Step key={`${index}-${consequence}`}>
              <StepBadge>{index + 1}</StepBadge>
              <span>{consequence}</span>
            </Step>
          ))}
        </Steps>
      )}
      {note && <Note>{note}</Note>}
    </>
  );
  const dismissButton = (
    <Button look="secondary" size="dialog" disabled={isPending} onClick={onDismiss}>
      {dismissLabel}
    </Button>
  );
  const confirmButton = (
    <Button
      look={isDestructive ? "destructive" : "commit"}
      size="dialog"
      disabled={isConfirmDisabled}
      isPending={isPending}
      pendingLabel={pendingLabel}
      onClick={onConfirm}
    >
      {confirmLabel}
    </Button>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      container={container}
      disableScrollLock={isScopedToContainer}
      transitionDuration={0}
      TransitionProps={{ onEntered: focusPanel }}
      aria-labelledby={titleId}
      sx={{
        position: isScopedToContainer ? "absolute" : "fixed",
        "& .MuiDialog-container": isPhone
          ? { alignItems: "stretch", paddingTop: 0 }
          : { alignItems: "flex-start", paddingTop: `${topOffset}px` },
      }}
      BackdropProps={{
        sx: {
          position: isScopedToContainer ? "absolute" : "fixed",
          backgroundColor: color.scrim,
        },
      }}
      PaperProps={{
        ref: panelRef,
        tabIndex: -1,
        "aria-modal": true,
        sx: isPhone
          ? {
              outline: "none",
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              maxHeight: "100%",
              margin: 0,
              padding: 0,
              borderRadius: 0,
              boxShadow: "none",
              backgroundColor: color.canvas,
            }
          : {
              outline: "none",
              boxSizing: "border-box",
              width: `${dialogWidth}px`,
              maxWidth: `calc(100% - ${2 * sideClearance}px)`,
              maxHeight: `calc(100% - ${topOffset + sideClearance}px)`,
              margin: 0,
              padding: `${dialogPadding}px`,
              gap: `${spaceLg}px`,
              borderRadius: `${radius.card}px`,
              boxShadow: shadow.raised,
              backgroundColor: color.surface,
            },
      }}
    >
      {isPhone ? (
        <>
          <TopBar data-testid="confirm-dialog-top-bar">
            <CloseControl type="button" aria-label="Close" disabled={isPending} onClick={onDismiss}>
              <svg
                aria-hidden="true"
                width={closeIconSize}
                height={closeIconSize}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </CloseControl>
            <TopBarTitle $isDestructive={isDestructive}>{eyebrow}</TopBarTitle>
          </TopBar>
          <StepContent>
            <Title id={titleId}>{title}</Title>
            {body}
          </StepContent>
          <BottomBar data-testid="confirm-dialog-bottom-bar">
            {confirmButton}
            {dismissButton}
          </BottomBar>
        </>
      ) : (
        <>
          <Heading>
            <Eyebrow $isDestructive={isDestructive}>{eyebrow}</Eyebrow>
            <Title id={titleId}>{title}</Title>
          </Heading>
          {body}
          <Footer>
            {dismissButton}
            {confirmButton}
          </Footer>
        </>
      )}
    </Dialog>
  );
};
